import {
  LoadingStates,
  propIsNotEmptyOrNil,
  isNotEmptyOrNil,
  propIsFalse,
  isNotEmpty,
} from '@pb/utils'
import {
  all,
  any,
  both,
  compose,
  curry,
  filter,
  find,
  lensProp,
  map,
  not,
  over,
  partition,
  path,
  pathOr,
  prop,
  propEq,
  reject,
  values,
} from 'ramda'
import { createSelector } from 'reselect'

import { CUSTOMER_TYPES, PAYMENT_PROVIDERS } from 'App/constants'
import appSelectors, {
  getIsSplitDeliveryEnabled,
  getIsSplitDeliveryEnabledMap,
  getMarketCode,
} from 'App/duck/selectors'
import clubCardSelectors from 'features/account/club-card/duck/selectors'
import {
  ALL_ITEMS_OUT_OF_STOCK_ERRORS,
  BOPIS_TYPE,
  CHECKOUT_ERROR_CODES,
  CHECKOUT_SECTIONS,
  CROSSBRANDS,
  CreditCardType,
  DELIVERY_METHODS,
  DELIVERY_TITLES,
  DROPSHIP_TYPE,
  HM_BRAND,
  KLARNA_PAY_LATER_PAYMENT_METHODS,
  OBSERVED_ADDRESS_FORM_ERRORS,
  PaymentMethod,
  RESELL_BRAND,
} from 'features/checkout/constants'
import { findDeliveryAddress } from 'features/checkout/helpers'
import {
  Address,
  AddressSuggestionsState,
  AdyenApplePayData,
  CheckoutState,
  DeliveryMode,
  GiftCard,
  Item,
  KlarnaData,
  KlarnaSession,
  NormalizedPickupPlace,
  Parcel,
  PaymentMode,
  PaymentState,
  PhoneNumber,
  PickupPlace,
  SavedCard,
  ConsignmentDeliveryData,
  NormalizedV2SplitOrder,
  V2Consignment,
  CybersourceCreditCardValues,
} from 'features/checkout/types'
import klarnaSelectors from 'features/klarna/duck/selectors'
import { OrderTotals } from 'features/orderTotals/types'
import toUppercasedBooleanString from 'helpers/toUppercasedBooleanString'
import { RootState } from 'redux/types'
import { ApiError, Price, XhrErrors } from 'types'

import isDeliveryNotBlacklisted from '../helpers/isDeliveryNotBlacklisted'
import { PageViewTrackingData } from '../types/tracking'
import { getTrackingProductsInParcel, getTrackingProductParcelTypes } from './operations'

const getCheckoutState = (state: RootState): CheckoutState => state.checkout
const getContextStatus = (state: RootState): LoadingStates => getCheckoutState(state).contextStatus
const getIsContextLoaded = (state: RootState): boolean =>
  getContextStatus(state) === LoadingStates.LoadedSuccess
const getCheckoutErrors = (state: RootState): ApiError[] => getCheckoutState(state).errors || []
const getOutOfStockItems = createSelector(
  getCheckoutState,
  (checkout) => checkout.outOfStockItems || [],
)
const getIsSomeItemsOutOfStock = createSelector(
  getOutOfStockItems,
  (outOfStockItems) => outOfStockItems.length > 0,
)

//--------
// Checkout Settings
//--------
const getDeliveryModesSettings = createSelector(getCheckoutState, (checkoutState) =>
  checkoutState.settings.deliverySettings
    ? checkoutState.settings.deliverySettings.zoneDeliveryModes
    : {},
)

const getPaymentModesSettings = createSelector(getCheckoutState, (checkoutState) =>
  checkoutState?.settings?.paymentSettings
    ? checkoutState.settings.paymentSettings.paymentModes
    : {},
)

const getCheckoutDelivery = (state: RootState): CheckoutState['delivery'] =>
  getCheckoutState(state).delivery || {}
const getDeliveryMethodBy = curry((state, deliveryMethod) =>
  pathOr({}, ['deliveryMethods', deliveryMethod], getCheckoutDelivery(state)),
)
const getCheckoutCustomer = (state: RootState): CheckoutState['customer'] =>
  getCheckoutState(state).customer || {}

const getXhrErrors = (state: RootState): XhrErrors => state.app.xhrErrorCodes

const getIsAllItemsOutOfStock = createSelector(
  getXhrErrors,
  getCheckoutErrors,
  (xhrErrors: XhrErrors, checkoutErrors) => {
    // prepare error will respond with ApiError and be added to the
    // general app.errorCodes state
    const xhrErrorCodes = Object.values(xhrErrors || {})
      .flat()
      .map((error) => error.errorCode)
    // proceed error will respond with a different error response
    // and the errorCode is never set on the app.xhrErrorCodes state
    // we need to get it from the checkout.errors state instead!
    const checkoutErrorCodes = checkoutErrors.map((error) => error.code)

    return any(
      (errorCode) => ALL_ITEMS_OUT_OF_STOCK_ERRORS.includes(errorCode),
      [...xhrErrorCodes, ...checkoutErrorCodes],
    )
  },
)

const getIsAllItemsResellItemsOutOfStock = createSelector(getXhrErrors, (xhrErrors: XhrErrors) => {
  const xhrErrorCodes = Object.values(xhrErrors || {})
    .flat()
    .map((error) => error.errorCode)

  return any(
    (errorCode) => CHECKOUT_ERROR_CODES.noFavoritesOnCartOOS === errorCode,
    [...xhrErrorCodes],
  )
})

const getIsResellAndOtherItemsOutOfStock = createSelector(getXhrErrors, (xhrErrors: XhrErrors) => {
  const xhrErrorCodes = Object.values(xhrErrors || {})
    .flat()
    .map((error) => error.errorCode)

  return any(
    (errorCode) => CHECKOUT_ERROR_CODES.mixFavoritesOnCartOOS === errorCode,
    [...xhrErrorCodes],
  )
})

const getShouldShowErrorScreen = (state: RootState): boolean =>
  getCheckoutState(state).shouldShowErrorScreen

const getAddressErrors = createSelector(getCheckoutErrors, (errors) =>
  errors.filter((error) =>
    (Object.values(OBSERVED_ADDRESS_FORM_ERRORS) as ReadonlyArray<string>).includes(error.code),
  ),
)

const getPrepareStatus = (state: RootState): LoadingStates => getCheckoutState(state).prepareStatus
const getPaymentData = createSelector(getCheckoutState, (checkout) => checkout.paymentData || {})

// ----------------------------------------
// CART
// ----------------------------------------
const getCart = (state: RootState): CheckoutState['cart'] => getCheckoutState(state).cart || {}
const getCartItems = (state: RootState): Item[] => getCart(state).items || []
const getCartItemsSortedByHazmat = (state: RootState): Item[] =>
  getCartItems(state)
    .slice(0)
    .sort((a, b) => (a.hazmat > b.hazmat ? -1 : 1))
const getCartHazmatItems = createSelector(getCartItems, filter(prop('hazmat')))
const getCartNonHazmatItems = createSelector(getCartItems, reject(prop('hazmat')))
const getCartUndeliverableHazmatItems = (state: RootState): Item[] =>
  getCart(state).hazmatItems || []
export const getHasUndeliverableHazmatItems = (state: RootState): boolean =>
  getCart(state).hazmatItems?.length > 0 || false
const getCartUniqueItemsCount = (state: RootState): number =>
  getCartItems(state).length + getCartUndeliverableHazmatItems(state).length
const getCartItemsQuantity = createSelector(
  getCartItems,
  getCartUndeliverableHazmatItems,
  (cartItems, undeliverableHazmatItems) => {
    const cartItemsQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0)
    const undeliverableHazmatItemsQuantity = undeliverableHazmatItems.reduce(
      (sum, item) => sum + item.quantity,
      0,
    )
    return cartItemsQuantity + undeliverableHazmatItemsQuantity
  },
)
const getIsAllCartItemsHazmat = (state: RootState): boolean =>
  getCartNonHazmatItems(state).length === 0

const getHasHazmatItems = (state: RootState): boolean =>
  getCartItems(state).filter((item) => item?.hazmat).length > 0

const getOrderTotals = (state: RootState): OrderTotals => state.orderTotals || {}

export const getDeliveryCost = createSelector(
  getOrderTotals,
  (orderTotals) => orderTotals.deliveryCost,
)
const getSubTotal = createSelector(getOrderTotals, (orderTotals) => orderTotals?.subTotal?.value)
const getCurrentBillingAddress = createSelector(getCart, (cart) => cart.billingAddress || {})
const getSelectedDeliveryAddress = (state: RootState): Address | undefined =>
  getCart(state).deliveryAddress
const getShouldShowProductsDetails = (state: RootState): boolean =>
  getCart(state).shouldShowProductsDetails || false

const getPreselectedPickupPlace = (state: RootState): string =>
  getCheckoutDelivery(state).preselectedPickupPlace

// ----------------
// Parcels
// ----------------

const getUI = createSelector(getCheckoutState, (checkout) => checkout.ui)
const getUIParcels = createSelector(getUI, (ui) => ui?.parcels)
export const getIsSplitOrderModalOpen = createSelector(
  getUIParcels,
  (parcels) => !!parcels.isSplitOrderModalOpen,
)
const getParcelDeliveryInfoMain = createSelector(
  getUIParcels,
  (parcels) => parcels.isMainDeliveryModalOpen,
)
const getParcelDeliveryInfoCrossbrands = createSelector(
  getUIParcels,
  (parcels) => parcels.isCrossbrandsDeliveryModalOpen,
)
const getParcelDeliveryInfoDropShip = createSelector(
  getUIParcels,
  (parcels) => parcels.isDropShipDeliveryModalOpen,
)
const getIsBOPISInformationModalOpen = createSelector(
  getUIParcels,
  (parcels) => parcels.isBOPISInformationModalOpen,
)

const getIsBOPISSplitOrderModalOpen = createSelector(
  getUIParcels,
  (parcels) => parcels?.isBOPISSplitOrderModalOpen,
)

const getBOPISStoreData = createSelector(getCart, (cart) => cart?.storeData)

const getSplitOrder = createSelector(
  getCheckoutDelivery,
  (delivery) => delivery.splitOrder || { parcels: [] },
)

const getParcelData = (parcel: ConsignmentDeliveryData, deliveryModeData: DeliveryMode) => {
  return {
    ...parcel.consignmentData,
    address: parcel.deliveryAddress,
    shippingCost: parcel?.deliveryMode?.deliveryCost,
    deliveryModeType: deliveryModeData?.type,
    deliveryModeShortDescription: deliveryModeData?.name,
    deliveryModeDaysDescription: deliveryModeData?.description,
    deliveryModeLongDescription: deliveryModeData?.longDescription,
    additionalDeliveryInfo: deliveryModeData?.additionalInfo,
    pssCode: deliveryModeData?.pssCode,
    id: parcel.id,
    carrierInfo: parcel?.deliveryMode?.carrierInfo,
    ...(parcel?.additionalStoreInfo && { additionalStoreInfo: parcel.additionalStoreInfo }),
    isGroupedParcel: parcel?.isGroupedParcel,
    isMainConsignment: parcel.consignmentData?.mainConsignment,
  }
}

const getParcels = createSelector(
  getCartItems,
  getSplitOrder,
  getDeliveryModesSettings,
  (cartItems, { parcels }, deliveryModesData) => {
    const consignments = parcels.map((parcel) => {
      const deliveryModeData = deliveryModesData[parcel.deliveryMode?.code]
      return getParcelData(parcel, deliveryModeData)
    })
    const findItems = map((entry: { variantCode?: string; quantity?: number } = {}) => ({
      ...find(propEq('variantCode', prop('variantCode', entry)), cartItems),
      ...entry,
    }))
    // @ts-expect-error FIXME
    return map(over(lensProp('entries'), findItems))(consignments || []) as Parcel[]
  },
)

const getCrossbrandsModalParcel = createSelector(getUIParcels, getParcels, (uiParcels, parcels) => {
  return parcels.find((parcel) => parcel.id === uiParcels.crossbrandsDeliveryModalParcelId)
})
const getHasParcels = createSelector(getParcels, (parcels) => parcels.length > 1)
const getItemsCountInParcels = createSelector(getParcels, (parcels) => {
  let totalItems = 0
  parcels.forEach((parcel) => {
    parcel.entries.forEach((entry) => {
      if (entry) {
        totalItems += entry.quantity
      }
    })
  })

  return totalItems
})
const getHasMultipleParcels = createSelector(getParcels, (parcels) => parcels.length > 1)

const getIsOnlyHMItems = createSelector(getParcels, (parcels) => {
  let isOnlyHM = true
  parcels.forEach((parcel) => {
    if (parcel.consignmentType && !HM_BRAND.includes(parcel.consignmentType)) isOnlyHM = false
  })
  return isOnlyHM
})

const getHmParcels = createSelector(getParcels, (parcels: Parcel[]) =>
  parcels.filter((parcel) => HM_BRAND.includes(parcel.consignmentType)),
)

const getDropShipParcels = createSelector(getParcels, (parcels: Parcel[]): Parcel[] =>
  parcels.filter((parcel) => parcel.consignmentType === DROPSHIP_TYPE),
)

const getAllCrossbrandParcels = createSelector(getParcels, (parcels: Parcel[]) =>
  (
    CROSSBRANDS.map(
      (crossbrand) => partition(propEq('consignmentType', crossbrand), parcels)[0],
    ) as [Parcel[]]
  ).filter((crossbrandParcels) => !!crossbrandParcels.length),
)

const getIsCrossBrandsBasket = createSelector(
  getAllCrossbrandParcels,
  (parcels) => parcels.length > 0,
)

// Hm Split Orders means its either a order orchestration, semi bulky or non-sortable order
export const getIsSplitHMOrder = createSelector(getHmParcels, (hmParcels) => hmParcels.length > 1)

// Multiple HM consignments from backend means order orchestration
export const getIsOrderOrchestration = createSelector(
  getCheckoutState,
  (checkoutState) =>
    (
      checkoutState.delivery.consignmentDatas?.filter(
        (consignment) => consignment.consignmentType === 'HM',
      ) || []
    ).length > 1,
)

const getIsDropShip = createSelector(
  getDropShipParcels,
  (dropShipParcels) => dropShipParcels.length > 0,
)

const getIsOnlyExternalItems = createSelector(getParcels, (parcels) => {
  let isOnlyExternal = true
  parcels.forEach((parcel) => {
    if (!parcel.consignmentType || HM_BRAND.includes(parcel.consignmentType)) isOnlyExternal = false
  })
  return isOnlyExternal
})

const getIsSingleExternalParcel = createSelector(
  getIsOnlyExternalItems,
  getParcels,
  (isOnlyExternalItems, parcels) =>
    // TODO This parcels.length === 0 check is to support V1 API when we have moved completely to V2, it can be removed
    (parcels.length === 1 || parcels.length === 0) && isOnlyExternalItems,
)

const getIsOnlyOrderOrchestration = createSelector(
  getIsOnlyHMItems,
  getParcels,
  (isOnlyHMItems, parcels) => parcels.length > 1 && isOnlyHMItems,
)

export const getDeliveryModeParcel = createSelector(
  [getSplitOrder, (_, parcelId) => parcelId],
  (splitOrder: NormalizedV2SplitOrder, parcelId?: string) =>
    splitOrder.parcels.find((parcel) => parcel.id === parcelId)?.deliveryMode?.code,
)

// BOPIS
export const getIsOnlyBOPISItems = createSelector(getParcels, (parcels) => {
  return (
    parcels.filter((parcel) => parcel.consignmentType !== BOPIS_TYPE).length === 0 &&
    parcels.filter((parcel) => parcel.consignmentType === BOPIS_TYPE).length > 0
  )
})

export const getHasBOPISItems = createSelector(getParcels, (parcels) => {
  return parcels.filter((parcel) => parcel.consignmentType === BOPIS_TYPE).length > 0
})

export const getUnavailableBOPISItems = createSelector(getCartItems, (items) => {
  return items.filter((item) => item?.unavailableBopis)
})

export const getHasMixedParcelsWithBOPIS = createSelector(getParcels, (parcels) => {
  return (
    parcels.filter((parcel) => parcel.consignmentType !== BOPIS_TYPE).length > 0 &&
    parcels.filter((parcel) => parcel.consignmentType === BOPIS_TYPE).length > 0
  )
})

const getBOPISParcel = createSelector(getParcels, (parcels) => {
  return parcels.find((parcel) => parcel.consignmentType === BOPIS_TYPE)
})

// ----------------------------------------
// DELIVERY
// ----------------------------------------
const getDeliveryPhoneNumber = (state: RootState): PhoneNumber | undefined =>
  getCheckoutDelivery(state).phoneNumber
const getSelectedDeliveryMethod = (state: RootState): string =>
  getCheckoutDelivery(state).selectedDeliveryMethod || ''
const getHomeDeliveryMethod = (state: RootState): string =>
  getDeliveryMethodBy(state, DELIVERY_METHODS.homeDelivery)
const getCurrentAddressById = (id: string, state: RootState): Address | {} =>
  pathOr({}, ['currentAddresses', id], getCheckoutCustomer(state))
const getSelectedHomeDeliveryAddressId = (state: RootState): string | null => {
  return pathOr(null, ['selectedDeliveryAddress'], getHomeDeliveryMethod(state))
}
const getSelectedHomeDeliveryAddress = (state: RootState): Address | {} => {
  const selectedAddressId = getSelectedHomeDeliveryAddressId(state)

  if (!selectedAddressId) {
    return {}
  }

  return getCurrentAddressById(selectedAddressId, state)
}
const getIsValidated = (state: RootState): boolean => getCheckoutDelivery(state).validated
const getAddressRequestStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).addressRequestStatus
const getCurrentAddresses = (state: RootState): Record<string, Address> =>
  pathOr({}, ['currentAddresses'], getCheckoutCustomer(state))

const getCurrentAddressesAsArray = createSelector(getCurrentAddresses, values)

const getCurrentDeliveryAddresses = createSelector(
  getCurrentAddressesAsArray,
  filter(prop('validAddressForDelivery')),
)

const getDeliveryAddress = createSelector(getCurrentAddresses, (currentAddresses) =>
  findDeliveryAddress(currentAddresses),
)

const getIsBillingAddressSameAsDelivery = createSelector(
  getCurrentAddresses,
  (currentAddresses) => {
    const deliveryAddress = Object.values(currentAddresses).find(
      (address) => address.shippingAddress && address.defaultForDelivery,
    )
    const billingAddress = Object.values(currentAddresses).find((address) => address.billingAddress)

    const hasSameAddress =
      deliveryAddress?.line1 === billingAddress?.line1 &&
      deliveryAddress?.line2 === billingAddress?.line2 &&
      deliveryAddress?.province === billingAddress?.province &&
      deliveryAddress?.district === billingAddress?.district &&
      deliveryAddress?.town === billingAddress?.town &&
      deliveryAddress?.postalCode === billingAddress?.postalCode &&
      deliveryAddress?.country === billingAddress?.country

    return hasSameAddress
  },
)

const getUpdateDeliveryModesStatus = createSelector(
  getCheckoutState,
  (checkout) => checkout.updateDeliveryModesStatus,
)

const getAddressSuggestionsState = (state: RootState): AddressSuggestionsState =>
  getCheckoutDelivery(state).addressSuggestions || {
    validatedAddresses: [],
    enteredAddress: undefined,
  }
const getValidatedAddresses = (state: RootState): Address[] =>
  getAddressSuggestionsState(state).validatedAddresses || []
export const getEnteredAddress = (state: RootState): Address | undefined =>
  getAddressSuggestionsState(state).enteredAddress

export const getUserEditedAddressFromSuggestions = createSelector(
  getAddressSuggestionsState,
  (addressSuggestion) => !!addressSuggestion?.userEditedAddressFromSuggestions,
)

const getDeliveryModeById = curry((state, id) =>
  pathOr(null, ['deliveryModes', id], getCheckoutDelivery(state)),
)
const getAllDeliveryModes = (state: RootState): Record<string, DeliveryMode> =>
  getCheckoutDelivery(state).deliveryModes || {}
const getDeliveryModesByDeliveryMethod = compose(pathOr([], ['deliveryModes']), getDeliveryMethodBy)
const getSelectedDeliverySpeedCode = (state: RootState): string =>
  getCheckoutDelivery(state).selectedDeliverySpeed || ''
const getSelectedDeliveryModeData = (state: RootState): DeliveryMode | undefined =>
  getCheckoutDelivery(state).selectedDeliveryModeData

const getSelectedDeliverySpeed = createSelector(
  getAllDeliveryModes,
  getSelectedDeliverySpeedCode,
  getSelectedDeliveryModeData,
  (allDeliveryModes, selectedDeliverySpeedCode, selectedDeliveryModeData): DeliveryMode => {
    if (selectedDeliverySpeedCode && selectedDeliverySpeedCode in allDeliveryModes)
      return allDeliveryModes[selectedDeliverySpeedCode]
    if (selectedDeliveryModeData) {
      return selectedDeliveryModeData
    }
    return { name: '', code: '', type: '', deliveryCost: {}, warehouseCutoffTimePassed: false }
  },
)

const getSelectedPickupPointCode = (state: RootState): string | undefined =>
  path(['selectedPickupPoint'], getDeliveryMethodBy(state, DELIVERY_METHODS.pickupPoint))
const getSelectedPickupPoint = (state: RootState): NormalizedPickupPlace | undefined => {
  const selectedPickupPointCode = getSelectedPickupPointCode(state)

  if (!selectedPickupPointCode) {
    return undefined
  }
  return path(
    ['pickupPoints', selectedPickupPointCode],
    getDeliveryMethodBy(state, DELIVERY_METHODS.pickupPoint),
  )
}
const getSelectedPickupPointDetailsFromRawResponse = (
  state: RootState,
): PickupPlace | undefined => {
  const selectedDeliverySpeedCode = getSelectedDeliverySpeedCode(state)
  const selectedPickupPointCode = getSelectedPickupPointCode(state)
  const { rawPickupPointsResponse }: { rawPickupPointsResponse: Record<string, PickupPlace[]> } =
    getDeliveryMethodBy(state, DELIVERY_METHODS.pickupPoint)

  return rawPickupPointsResponse[selectedDeliverySpeedCode]?.find(
    (deliveryMode) => deliveryMode.code === selectedPickupPointCode,
  )
}

const getSelectedCnCPointCode = (state: RootState): string | undefined =>
  path(['selectedCnCPoint'], getDeliveryMethodBy(state, DELIVERY_METHODS.clickAndCollect))
const getSelectedCnCPoint = (state: RootState): NormalizedPickupPlace | undefined => {
  const selectedCnCPointCode = getSelectedCnCPointCode(state)

  if (!selectedCnCPointCode) {
    return undefined
  }

  return path(
    ['CnCPoints', selectedCnCPointCode],
    getDeliveryMethodBy(state, DELIVERY_METHODS.clickAndCollect),
  )
}

export const getSelectedPickupPlacesPerParcel = createSelector(
  getCheckoutDelivery,
  (checkoutDelivery) => checkoutDelivery?.deliveryMethods?.selectedPickupPlacePerParcel || {},
)

const isNotDisabled = compose(
  not,
  both(propIsNotEmptyOrNil('deliveryDateDescription'), propIsFalse('disableGrayOutLogic')),
)
const getDeliveryModesBySelectedDeliveryLocation = createSelector(
  getCheckoutDelivery,
  getSelectedDeliveryMethod,
  getSelectedPickupPoint,
  getSelectedCnCPoint,
  (delivery, selectedDeliveryMethod, selectedPickupPoint, selectedCnCPoint) => {
    const getDeliveryModesByCode = (code) => pathOr([], ['deliveryModes', code], delivery)
    let selectedDeliveryModeCodes
    switch (selectedDeliveryMethod) {
      case DELIVERY_METHODS.pickupPoint:
        selectedDeliveryModeCodes = selectedPickupPoint?.deliveryModes || []
        break
      case DELIVERY_METHODS.clickAndCollect:
        selectedDeliveryModeCodes = selectedCnCPoint?.deliveryModes || []
        break
      default:
        selectedDeliveryModeCodes = pathOr(
          [],
          ['deliveryMethods', selectedDeliveryMethod || '', 'deliveryModes'],
          delivery,
        )
    }
    return map(getDeliveryModesByCode, selectedDeliveryModeCodes)
  },
)

const getFilteredDeliverySpeeds = (
  deliveryModes,
  hasHazmatItems,
  isOnlyExternalItems,
): DeliveryMode[] => {
  const isSupportingHazmatStatus = (deliverySpeeds) =>
    hasHazmatItems ? filter(prop('supportHazmat'), deliverySpeeds) : deliverySpeeds

  const externalBrandDelivery = (deliverySpeeds) =>
    isOnlyExternalItems ? deliverySpeeds : reject(prop('externalBrandDelivery'), deliverySpeeds)

  return compose(
    filter(isNotDisabled),
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    filter(isDeliveryNotBlacklisted),
    isSupportingHazmatStatus,
    externalBrandDelivery,
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
  )(deliveryModes) as DeliveryMode[]
}

const getAvailableDeliverySpeeds = createSelector(
  getDeliveryModesBySelectedDeliveryLocation,
  getHasHazmatItems,
  getIsOnlyExternalItems,
  (deliveryModes = [], hasHazmatItems, isOnlyExternalItems) => {
    return getFilteredDeliverySpeeds(deliveryModes, hasHazmatItems, isOnlyExternalItems)
  },
)

const getIsDeliverySpeedSelectedByCode = curry(
  (id, state) => id === getSelectedDeliverySpeedCode(state),
)
const getIsDeliverySpeedSelected = createSelector(
  getSelectedDeliverySpeed,
  getSelectedDeliveryMethod,
  (selectedDeliverySpeed, selectedDeliveryMethod) =>
    selectedDeliverySpeed.type === selectedDeliveryMethod,
)

const getIsCustomerSelectedSpeed = (state: RootState): boolean =>
  getCheckoutDelivery(state).isCustomerSelectedSpeed
const getIsDeliveryMethodSupportingHazmat = (state: RootState): boolean => {
  const availableDeliverySpeeds = getAvailableDeliverySpeeds(state)
  const hasHazmatSupportingSpeed = (availableDeliverySpeeds as any).some(
    (deliverySpeed) => deliverySpeed.supportHazmat,
  )
  return hasHazmatSupportingSpeed
}
const getIsSelectedDeliverySpeedSupportingHazmat = (state: RootState): boolean => {
  const selectedDeliverySpeed = getSelectedDeliverySpeed(state)
  return !!selectedDeliverySpeed.supportHazmat
}
const getShouldChangeDeliverySpeed = (state: RootState): boolean => {
  const selectedDeliverySpeed = getSelectedDeliverySpeedCode(state)
  const isAllCartItemsHazmat = getIsAllCartItemsHazmat(state)
  const isSelectedDeliverySpeedSupportingHazmat = getIsSelectedDeliverySpeedSupportingHazmat(state)
  const isDeliveryMethodSupportingHazmat = getIsDeliveryMethodSupportingHazmat(state)
  return (
    !!selectedDeliverySpeed &&
    isAllCartItemsHazmat &&
    isDeliveryMethodSupportingHazmat &&
    !isSelectedDeliverySpeedSupportingHazmat
  )
}
const getShouldChangeDeliveryMethod = (state: RootState): boolean => {
  const isAllCartItemsHazmat = getIsAllCartItemsHazmat(state)
  const isDeliveryMethodSupportingHazmat = getIsDeliveryMethodSupportingHazmat(state)
  const availableDeliverySpeeds = getAvailableDeliverySpeeds(state)
  const isHazmatInvalid = isAllCartItemsHazmat && !isDeliveryMethodSupportingHazmat
  return isHazmatInvalid || (availableDeliverySpeeds as any).length === 0
}
const getDeliverySpeedSelectionStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).deliverySpeedSelectionStatus
const getIsDeliveryValidated = (state: RootState): boolean => getCheckoutDelivery(state).validated

const getAllDeliveryModesAsArray = createSelector(
  getCheckoutDelivery,
  getHasHazmatItems,
  getIsOnlyExternalItems,
  (delivery, hasHazmatItems, isOnlyExternalItems) => {
    const { deliveryModesAsArray = [] } = delivery
    return getFilteredDeliverySpeeds(deliveryModesAsArray, hasHazmatItems, isOnlyExternalItems)
  },
)

const getIsValidDeliverySpeedSelected = createSelector(
  getSelectedDeliverySpeedCode,
  getAllDeliveryModesAsArray,
  (selectedDeliverySpeedCode, deliveryModes: DeliveryMode[]) => {
    return !!deliveryModes.find(({ code }) => code === selectedDeliverySpeedCode)
  },
)

const getIsDeliverySpeedSectionCompleted = createSelector(
  getIsDeliveryValidated,
  getSelectedDeliverySpeed,
  getIsValidDeliverySpeedSelected,
  getUpdateDeliveryModesStatus,
  getIsOnlyBOPISItems,
  (
    isDeliveryValidated,
    selectedDeliverySpeed,
    isValidDeliverySpeedSelected,
    updateDeliveryModesStatus,
    isOnlyBopisItems,
  ) => {
    if (updateDeliveryModesStatus === LoadingStates.LoadedSuccess && !isOnlyBopisItems) {
      return (
        isDeliveryValidated && isNotEmpty(selectedDeliverySpeed) && isValidDeliverySpeedSelected
      )
    }

    return isDeliveryValidated
  },
)
const getIsAddressCompletelyBlacklisted = (state: RootState): boolean => {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 2.
  const deliveryModeCodes = getDeliveryModesByDeliveryMethod(state, DELIVERY_METHODS.homeDelivery)
  const homeDeliveryModes = map(getDeliveryModeById(state), deliveryModeCodes)
  return (
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    isNotEmptyOrNil(homeDeliveryModes) && all(prop('addressBlackListed'), homeDeliveryModes)
  )
}

const getShouldShowDeliveryDetails = createSelector(
  getIsDeliveryValidated,
  getIsOnlyOrderOrchestration,
  (isDeliveryValidated, isOnlyOrderOrchestration) =>
    isDeliveryValidated && !isOnlyOrderOrchestration,
)

const getIsCODSupported = createSelector(
  getSelectedDeliverySpeed,
  getSelectedPickupPoint,
  getSelectedCnCPoint,
  (deliverySpeed, pickUpPoint, cncPoint): boolean => {
    switch (deliverySpeed.type) {
      case DELIVERY_METHODS.homeDelivery:
        return !!deliverySpeed.supportCOD
      case DELIVERY_METHODS.pickupPoint:
        return !!(pickUpPoint ? pickUpPoint.supportCOD : deliverySpeed.supportCOD)
      case DELIVERY_METHODS.clickAndCollect:
        return !!(cncPoint ? cncPoint.supportCOD : deliverySpeed.supportCOD)
      default:
        return true
    }
  },
)

export const getHasInvalidDeliveryAddress = createSelector(
  getCurrentAddresses,
  getIsDeliveryValidated,
  (currentAddresses, deliveryValidated) => {
    const listOfUserAddresses = currentAddresses ? Object.values(currentAddresses) : []
    return (
      !deliveryValidated &&
      listOfUserAddresses.some(
        (address) => address?.shippingAddress && !address?.validAddressForDelivery,
      ) &&
      !listOfUserAddresses.some((address) => address?.defaultForDelivery)
    )
  },
)

// ----------------------------------------
// PICKUP POINTS
// ----------------------------------------

const getPreviouslyUsedPickupPlace = (state: RootState): NormalizedPickupPlace | undefined =>
  getCheckoutDelivery(state).previouslyUsedPickupPlace

const getPickupPointsWithKeys = createSelector(
  getCheckoutDelivery,
  (checkoutDelivery) =>
    checkoutDelivery?.deliveryMethods?.[DELIVERY_METHODS.pickupPoint]?.pickupPoints || {},
)
const getPickupPoints = createSelector(getPickupPointsWithKeys, (pickupPoints) => {
  return Object.values(pickupPoints).sort((pA, pB) => {
    // sort by distance, but if it's equal then sort by index instead
    if (pA.distance && pB.distance) {
      if (pA.distance === pB.distance && pA.index && pB.index) {
        return pA.index - pB.index
      }
      return pA.distance - pB.distance
    }
    if (pA.index && pB.index) {
      return pA.index - pB.index
    }
    return 0
  })
})
const getAvailablePickupPoints = createSelector(
  getIsAllCartItemsHazmat,
  getPickupPoints,
  (isAllCartItemsHazmat, pickupPoints) =>
    pickupPoints
      .filter((pickupPoint) => (isAllCartItemsHazmat ? pickupPoint.supportHazmat : true))
      .filter(isDeliveryNotBlacklisted),
)
const getPickupPointByCode = createSelector(
  getPickupPointsWithKeys,
  (pickupPoints) => (code) => pickupPoints[code],
)
const getPickupPointsStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).pickupPointsStatus
const getLastUsedPickupPlacePostalCode = (state: RootState): string | undefined =>
  getCheckoutDelivery(state).lastUsedPickupPlacePostalCode
const getNearestPickupPoints = createSelector(
  getPickupPoints,
  getPreviouslyUsedPickupPlace,
  (pickupPoints, previouslyUsedPickupPoint) =>
    pickupPoints.filter(
      (pickupPoint: NormalizedPickupPlace) => pickupPoint.code !== previouslyUsedPickupPoint?.code,
    ),
)
const getShouldShowPreviouslyUsedPickupPoint = createSelector(
  getPickupPoints,
  getPreviouslyUsedPickupPlace,
  (pickupPoints, previouslyUsedPickupPoint) => {
    if (!previouslyUsedPickupPoint) {
      return false
    }
    return !!pickupPoints.find(({ code }) => code === previouslyUsedPickupPoint.code)
  },
)
// ----------------------------------------
// Click And Collect POINTS
// ----------------------------------------
const getCnCPointsWithKeys = createSelector(
  getCheckoutDelivery,
  (checkoutDelivery) =>
    checkoutDelivery?.deliveryMethods?.[DELIVERY_METHODS.clickAndCollect]?.CnCPoints || {},
)
const getCnCPoints = createSelector(getCnCPointsWithKeys, (cncPoints) => Object.values(cncPoints))
const getAvailableCnCPoints = createSelector(
  getIsAllCartItemsHazmat,
  getCnCPoints,
  (isAllCartItemsHazmat, CnCPoints) =>
    CnCPoints.filter((CnCPoint) => (isAllCartItemsHazmat ? CnCPoint.supportHazmat : true)).filter(
      isDeliveryNotBlacklisted,
    ),
)
const getCnCPointByCode = createSelector(
  getCnCPointsWithKeys,
  (CnCPoints) => (code) => CnCPoints[code],
)
const getCnCPointsStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).CnCPointsStatus
const getNearestCnCPoints = createSelector(
  getCnCPoints,
  getPreviouslyUsedPickupPlace,
  (CnCPoints, previouslyUsedCnCPoint) =>
    CnCPoints.filter((CnCPoint) => CnCPoint.code !== previouslyUsedCnCPoint?.code),
)
const getShouldShowPreviouslyUsedCnCPoint = createSelector(
  getCnCPoints,
  getPreviouslyUsedPickupPlace,
  (CnCPoints, previouslyUsedCnCPoint) => {
    if (!previouslyUsedCnCPoint) {
      return false
    }
    return !!CnCPoints.find(({ code }) => code === previouslyUsedCnCPoint.code)
  },
)
// ----------------------------------------
// CUSTOMER DETAILS
// ----------------------------------------
const getLoyaltyStatus = createSelector(getCheckoutCustomer, (customer) => customer.loyaltyStatus)
const getHasCheckoutCustomerData = (state: RootState): boolean =>
  isNotEmpty(getCheckoutCustomer(state))
const getHasCheckoutOrderTotalsData = (state: RootState): boolean =>
  isNotEmpty(getOrderTotals(state))
const getIsReturningCustomer = (state: RootState): boolean => getCheckoutCustomer(state).validated
const getCustomerDetailsStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).customerDetailsStatus
const getCheckoutCustomerPostalCode = (state: RootState): string =>
  getCheckoutCustomer(state).postalCode
const getNewsletterSubscription = (state: RootState): boolean =>
  getCheckoutCustomer(state).newsletterSubscription || false
const getCustomerCellPhone = (state: RootState) => getCheckoutCustomer(state).cellPhone
const getCustomerPrefix = (state: RootState) => getCheckoutCustomer(state).prefix
const getCustomerPhoneNumber = createSelector(
  getCustomerPrefix,
  getCustomerCellPhone,
  (prefix, cellPhone): PhoneNumber => ({ prefix, cellPhone }),
)
const getIsReturningGuest = (state: RootState): boolean => !!getCheckoutCustomer(state).savedAsGuest
const getIsNewGuest = createSelector(
  appSelectors.getCustomerType,
  getIsReturningGuest,
  (customerType, isReturningGuest) => {
    return customerType === CUSTOMER_TYPES.GUEST && !isReturningGuest
  },
)
const getIsGuestCustomer = createSelector(
  getIsNewGuest,
  getIsReturningGuest,
  (isNewGuest, isReturningGuest) => {
    return isNewGuest || isReturningGuest
  },
)
// ----------------------------------------
// PAYMENT
// ----------------------------------------
const getCheckoutPayment = (state: RootState): PaymentState => getCheckoutState(state).payment
const getPaymentError = (state: RootState): ApiError =>
  getCheckoutPayment(state).paymentError || null
const getSelectedPaymentMethodCode = (state: RootState): PaymentMethod => {
  const paymentMethod = getCheckoutPayment(state).selectedPaymentMethod
  return paymentMethod?.code
}

const getSelectedPaymentMethod = (state: RootState): PaymentMode =>
  getCheckoutPayment(state).selectedPaymentMethod

const getAvailablePaymentMethods = createSelector(
  getCheckoutPayment,
  getCheckoutCustomer,
  appSelectors.getKlarnaMinAge,
  (checkoutPayment, customer, klarnaMinAge) => {
    const { age } = customer
    const { availablePaymentMethods = [] } = checkoutPayment

    const hasUserKlarnaLegalAge = ({ paymentProvider }): boolean => {
      if (paymentProvider === PAYMENT_PROVIDERS.KLARNA) {
        if (age < klarnaMinAge) {
          return false
        }
      }
      return true
    }
    return availablePaymentMethods.filter(hasUserKlarnaLegalAge)
  },
)

const getOmnicreditAuthCallbackEnabled = createSelector(
  getPaymentData,
  getSelectedPaymentMethodCode,
  (paymentData, code) => {
    return paymentData.klarnaData?.omnicreditAuthCallbackEnabled && code === PaymentMethod.PayNow
  },
)

const createPaymentMethodDetailsSelector = (paymentMethodCode: PaymentMethod) =>
  createSelector(
    getAvailablePaymentMethods,
    (paymentMethods: PaymentMode[]): PaymentMode | undefined => {
      return paymentMethods.find((paymentMethod) => paymentMethod.code === paymentMethodCode)
    },
  )
const getCashOnDeliveryDetails = createPaymentMethodDetailsSelector(PaymentMethod.CashOnDelivery)
const getSelectedPaymentMethodDetails = createSelector(
  getSelectedPaymentMethod,
  getSelectedPaymentMethodCode,
  getCheckoutPayment,
  (selectedMethod, code, payment) => {
    const price = pathOr(0, ['standardPaymentModeValue', 'value'], selectedMethod)
    const name = selectedMethod?.name
    const paymentProvider = selectedMethod?.paymentProvider
    let selectable = selectedMethod && selectedMethod.selectable !== false
    if (payment.isPurchaseCoveredByGiftCards) selectable = true
    return { price, name, paymentProvider, code, selectable }
  },
)
const getPatchPaymentStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).patchPaymentStatus

const getIsPaymentValidated = (state: RootState): boolean => getCheckoutPayment(state).validated

const getIsPaymentUserInputNeeded = (state: RootState): boolean =>
  getCheckoutPayment(state).paymentUserInputNeeded
const getHasKlarnaPaymentMethod = createSelector(
  getAvailablePaymentMethods,
  (availablePaymentMethods) =>
    availablePaymentMethods.find((method) => method.paymentProvider === PAYMENT_PROVIDERS.KLARNA),
)
const getKlarnaData = createSelector(getPaymentData, (paymentData) => paymentData.klarnaData || {})
const getKlarnaUpdateSessionBody = createSelector(
  getKlarnaData,
  (klarnaData: KlarnaData): KlarnaSession | null =>
    'updateSessionBodyObj' in klarnaData ? klarnaData.updateSessionBodyObj : null,
)
const getSavedCards = (state: RootState): SavedCard[] => getCheckoutPayment(state).savedCards || []
const getAdyenCreditCardValues = (state: RootState) =>
  getCheckoutPayment(state).adyenCreditCardValues
const getCybersourceCreditCardValues = (state: RootState): CybersourceCreditCardValues =>
  getCheckoutPayment(state).cybersourceCreditCardValues
const getSelectedPaymentCardId = (state: RootState): string | null =>
  getCheckoutPayment(state).selectedPaymentCardId
const getNewPaymentCardType = (state: RootState): CreditCardType =>
  getCheckoutPayment(state).newPaymentCardType
const getNewPaymentCardLastFourDigits = (state: RootState): string =>
  getCheckoutPayment(state).newPaymentCardLastFourDigits
export const getPreSelectedBankId = (state: RootState): string | undefined =>
  getCheckoutPayment(state).preSelectedBankId

export const getIsPaypalSelected = (state: RootState): boolean =>
  getSelectedPaymentMethodCode(state) === PaymentMethod.PayPal

const getSelectedPaymentCardInfo = createSelector(
  getSavedCards,
  getSelectedPaymentCardId,
  getNewPaymentCardType,
  getNewPaymentCardLastFourDigits,
  (
    savedCards,
    cardId,
    cardType,
    lastFourDigits,
  ): SavedCard | Pick<SavedCard, 'cardType' | 'lastFourDigits'> | undefined => {
    if (cardId) {
      const cardInfo = savedCards.find((card) => card.id === cardId)
      return cardInfo
    }

    return {
      cardType,
      lastFourDigits,
    }
  },
)
const getAdyenApplePayPaymentData = (state: RootState): AdyenApplePayData | undefined =>
  getCheckoutPayment(state).adyenApplePayPaymentMethodData || {}
const getApplePayAuthToken = (state: RootState): string => state.applePay.applePayAuthToken || ''

// ----------------------------------------
// NO GENRE
// ----------------------------------------
const getIsPaymentInitiallyCompleted = createSelector(
  getCheckoutPayment,
  ({ validatedPayment, validatedBilling, paymentUserInputNeeded }) => {
    return validatedBilling && validatedPayment && !paymentUserInputNeeded
  },
)

const getIsBillingAddressInitiallyCompleted = createSelector(
  getCheckoutPayment,
  ({ validatedBilling }) => validatedBilling,
)

export const getIsAddressValidForDelivery = createSelector(
  getCurrentAddresses,
  (currentAddresses) =>
    Object.values(currentAddresses).find((f) => f?.shippingAddress)?.validAddressForDelivery,
)

const getCompletedSections = createSelector(
  getCheckoutCustomer,
  getCheckoutDelivery,
  getIsDeliverySpeedSectionCompleted,
  getIsPaymentInitiallyCompleted,
  getIsBillingAddressInitiallyCompleted,
  getHasBOPISItems,
  getDeliveryPhoneNumber,
  getIsAddressValidForDelivery,
  (
    customer,
    delivery,
    isDeliverySpeedSelected,
    isPaymentInitiallyCompleted,
    isBillingAddressInitiallyCompleted,
    hasBopisItem,
    deliveryPhoneNumber,
    validDeliveryAddress,
  ): string[] => {
    const isBopisWithoutPhoneNumber = hasBopisItem && !deliveryPhoneNumber
    return [
      ...(customer.validated && isBillingAddressInitiallyCompleted
        ? [CHECKOUT_SECTIONS.myInfoAndBilling]
        : []),
      ...(customer.validated ? [CHECKOUT_SECTIONS.myInformation] : []),
      ...(delivery.validated &&
      isDeliverySpeedSelected &&
      !isBopisWithoutPhoneNumber &&
      validDeliveryAddress
        ? [CHECKOUT_SECTIONS.delivery]
        : []),
      // added payment.validated check, because validatedPayment is true from the beginning, so it makes flow to "skip" the payment section
      ...(isPaymentInitiallyCompleted ? [CHECKOUT_SECTIONS.payment] : []),
      ...(isBillingAddressInitiallyCompleted ? [CHECKOUT_SECTIONS.billingAddress] : []),
    ]
  },
)

export const getHasUndeliverableItems = createSelector(getUI, (ui) => {
  return ui?.hasUndeliverableItems
})

export const isDeliveryValidated = createSelector(getCheckoutDelivery, (delivery) => {
  return delivery?.validated
})

const getIsMyInformationSectionComplete = createSelector(
  getCheckoutCustomer,
  (customer) => customer.validated,
)

const getIsGuestReadyToCheckoutWithKlarna = createSelector(
  getIsGuestCustomer,
  getCheckoutPayment,
  getSelectedPaymentMethodDetails,
  klarnaSelectors.getIsMemberInfoValidated,
  (isGuest, payment, selectedPaymentMethod, isMemberInfoValidated) => {
    const { paymentProvider, code } = selectedPaymentMethod
    return isGuest &&
      paymentProvider === PAYMENT_PROVIDERS.KLARNA &&
      KLARNA_PAY_LATER_PAYMENT_METHODS.includes(code)
      ? isMemberInfoValidated
      : true
  },
)

const getIsReadyToPlaceOrder = createSelector(
  getCheckoutCustomer,
  getCheckoutDelivery,
  getCheckoutPayment,
  getSelectedPaymentMethodDetails,
  klarnaSelectors.getAllPaymentMethodCategories,
  getIsGuestReadyToCheckoutWithKlarna,
  (
    customer,
    delivery,
    payment,
    selectedPaymentMethod,
    klarnaPaymentMethods,
    isGuestReadyToCheckoutWithKlarna,
  ) => {
    const isChoosenKlarnaPaymentDenied =
      selectedPaymentMethod.paymentProvider === PAYMENT_PROVIDERS.KLARNA &&
      (klarnaPaymentMethods as any)[selectedPaymentMethod.code]?.showForm === false
    if (isChoosenKlarnaPaymentDenied) {
      return false
    }
    return (
      customer.validated &&
      delivery.validated &&
      (payment.validated || (payment.validatedPayment && payment.validatedBilling)) &&
      isGuestReadyToCheckoutWithKlarna
    )
  },
)
const getPostProceedStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).postProceedStatus

const getRequestStatusByDeliveryMethod = createSelector(
  [getCheckoutState, (_, methodType: string | undefined) => methodType],
  (checkoutState, methodType) => {
    if (methodType === DELIVERY_METHODS.pickupPoint) {
      return checkoutState.pickupPointsStatus
    }
    if (methodType === DELIVERY_METHODS.clickAndCollect) {
      return checkoutState.CnCPointsStatus
    }

    return null
  },
)

// ----------------------------------------
// GIFT CARDS
// ----------------------------------------
const getGiftCards = (state: RootState): GiftCard[] => getCheckoutPayment(state).giftCardList || []
const getGiftCardsStatus = (state: RootState): LoadingStates =>
  getCheckoutState(state).giftCardsStatus
const getGiftCardTotalAmount = (state: RootState): Price | undefined =>
  getOrderTotals(state).totalGiftCardAmount
const getIsPurchaseCoveredByGiftCards = (state: RootState): boolean =>
  getCheckoutPayment(state).isPurchaseCoveredByGiftCards
const getIsSelectedDeliveryPlaceSupportHazmat = createSelector(
  getSelectedDeliveryMethod,
  getSelectedPickupPoint,
  (selectedDeliveryMethod, pickupPoint) => {
    if (selectedDeliveryMethod === DELIVERY_METHODS.pickupPoint) {
      return pickupPoint?.supportHazmat
    }
    return true
  },
)

// BE doesn't always return cardholder address, because:
// newPaymentInfo.address is populated only if you call an address POST with PAYMENT type
// If it's empty during proceed, billing address will be used instead
const getCardholderAddress = (state: RootState): Address =>
  getCheckoutPayment(state).cardholderAddress || getCurrentBillingAddress(state)

const getPrepopulatedPostalCode = createSelector(
  getLastUsedPickupPlacePostalCode,
  (_, { previouslyUsedPickupPlace }) => previouslyUsedPickupPlace,
  getDeliveryAddress,
  getMarketCode,
  (
    lastUsedPickupPlacePostalCode,
    previouslyUsedPickupPlace,
    deliveryAddress,
    marketCode,
  ): string => {
    // TODO: should be renamed to lastTypedPickupPlacePostalCode ?
    if (lastUsedPickupPlacePostalCode) {
      return lastUsedPickupPlacePostalCode
    }
    if (previouslyUsedPickupPlace) {
      return path(['postalCode'], previouslyUsedPickupPlace) || ''
    }
    if (deliveryAddress?.country === marketCode) {
      return deliveryAddress?.postalCode || ''
    }

    return ''
  },
)

const getDeliveryTitle = createSelector(
  getSelectedDeliveryMethod,
  getSelectedDeliverySpeed,
  (selectedDeliveryMethod, selectedDeliverySpeed) => {
    const selectedMethod = selectedDeliveryMethod || selectedDeliverySpeed.type
    return DELIVERY_TITLES[selectedMethod || '']
  },
)

// ----------------
// Edit Item quantity USED IN EXPERIMENT
// ----------------
const getEditItemQuantityStatus = (state: RootState): { status: LoadingStates; id: string } =>
  getCheckoutState(state).editItemQuantityStatus

const getParcelToShowInProductDetails = (state: RootState): string =>
  getCheckoutState(state).parcelToShowInProductDetails
// ----------------
// Tracking
// ----------------
const getCustomerTrackingData = createSelector(
  appSelectors.getIsUserClubMember,
  clubCardSelectors.getCurrentTier,
  appSelectors.getIsSignedIn,
  appSelectors.getCustomerType,
  appSelectors.getUserBpid,
  appSelectors.getIsFashionNewsEnabled,
  appSelectors.getUserPostalCode,
  appSelectors.getUserEmail,
  appSelectors.getUserCountry,
  appSelectors.getUserCity,
  appSelectors.getUserId,
  appSelectors.getUserState,
  (
    isUserClubMember,
    currentTier,
    isSignedIn,
    customer_type,
    customer_bpid,
    customer_fashion_news,
    customer_zip,
    customer_email,
    customer_country,
    customer_city,
    customer_id,
    customer_state,
  ) => ({
    customer_id,
    customer_username: customer_email,
    customer_city,
    customer_country,
    customer_email,
    customer_state,
    customer_zip,
    customer_fashion_news,
    customer_type,
    customer_bpid,
    customer_status: toUppercasedBooleanString(isSignedIn),
    customer_club_member: toUppercasedBooleanString(isUserClubMember),
    ...(toUppercasedBooleanString(isUserClubMember) && {
      customer_club_level: currentTier === 0 ? 'tier 1' : 'tier 2',
    }),
    ...(customer_type && { dep_variable_name: ['customer_type'] }),
    ...(customer_type && { dep_variable_format: ['string'] }),
  }),
)
const getProductsTrackingData = createSelector(
  getCartItems,
  getOrderTotals,
  appSelectors.getCurrentCurrency,
  getParcels,
  (items, orderTotals, currentCurrency = {}, parcels): PageViewTrackingData => {
    const { totalDiscount, clubVoucherDiscount } = orderTotals
    const getProductBaseCode = (item: Item): string =>
      pathOr('', ['baseProductCode'], item).substring(0, 7)
    const getProductRecommendedSize = (item: Item): string[] => {
      const { size } = item
      const baseProductCode = getProductBaseCode(item)
      const recommendedSize =
        sessionStorage.getItem(`recsize-${baseProductCode}`) || 'No recommendation available'
      return [recommendedSize, size]
    }

    return {
      product_id: items.map((item) => getProductBaseCode(item)),
      product_article_id: items.map((item) => item.productCode),
      product_name: items.map((item) => pathOr('', ['internalName'], item).toUpperCase()),
      product_category: items.map((item) => pathOr('', ['categoryCode'], item).toUpperCase()),
      product_cart_starter: items.map((item) =>
        toUppercasedBooleanString(item.isCartStarter || false),
      ),
      product_color_code: items.map((item) => item.colorCode),
      product_size_code: items.map((item) => item.sizeCode),
      product_list_price: items.map((item) =>
        pathOr('', ['productPrice', 'value'], item).toString(),
      ),
      product_currency: items.map(() => currentCurrency.isocode),
      product_quantity: items.map((item) => item.quantity.toString()),
      product_price_type: items.map((item) => item.priceType),
      product_original_price: items.map((item) => pathOr('', ['whitePrice', 'value'], item)),
      product_size_rec: items.map((item) => getProductRecommendedSize(item)),
      product_size: items.map((item) => item.size),
      product_oos: items.map((item) => toUppercasedBooleanString(item.inStock)),
      product_discount_amount: totalDiscount?.value || 0,
      product_voucher_amount: clubVoucherDiscount?.value || 0,
      product_brand: parcels
        .map((parcel) =>
          parcel.brand
            ? // TODO - remove this and only use latter part once v2 is implemented on all markets - [COWEB-2349]
              Array(parcel.entries.length).fill(parcel.brand)
            : parcel.entries.map((entry) => entry.brand || 'H&M'),
        )
        .flat(),
      product_parcel: getTrackingProductsInParcel(items, parcels),
      product_parcel_type: getTrackingProductParcelTypes(items, parcels),
      checkout_parcel: parcels.length,
      product_osa_area: items.map((item) =>
        pathOr('', ['onSiteAttributeMap', 'product_osa_area'], item),
      ),
      product_osa_type: items.map((item) =>
        pathOr('', ['onSiteAttributeMap', 'product_osa_type'], item),
      ),
      product_video: items.map((item) =>
        toUppercasedBooleanString(pathOr('', ['onSiteAttributeMap', 'productVideo'], item)),
      ),
      imp_origin_system: items.map(
        (item: Item) => item.onSiteAttributeMap?.product_pra_origin || '',
      ),
      product_marker_marketing: items.map(({ condition }) =>
        condition ? `Condition_${condition}` : 'FALSE',
      ),
      dep_variable_name: ['checkout_parcel'],
      dep_variable_format: ['integer'],
    }
  },
)

const getVatNumber = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState.additionalInfo.customerVatNumber,
)

const getVatInvoiceType = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState.additionalInfo.customerVatInvoiceType,
)

const getHasResellItem = createSelector(getOutOfStockItems, (items) => {
  let hasResell = false
  items.forEach((item) => {
    if (item.brand && item.brand === RESELL_BRAND) {
      hasResell = true
    }
  })
  return hasResell
})

const getHasOnlyResellItem = createSelector(getOutOfStockItems, (items) => {
  if (items.length === 0) return false
  let hasOnlyResell = true
  items.forEach((item) => {
    if (item.brand !== RESELL_BRAND) {
      hasOnlyResell = false
    }
  })
  return hasOnlyResell
})

const getResellItemsInCart = createSelector(getCartItems, (items: Item[]): Item[] | [] => {
  if (items.length === 0) return []
  return items.filter((item) => item.brand?.toUpperCase() === RESELL_BRAND)
})

// BankTransfer

const getIsBankTransferSelected = createSelector(getCheckoutState, ({ payment }) => {
  const paymentMode = payment.selectedPaymentMethod
  return (
    paymentMode?.code === PaymentMethod.BankTransfer || paymentMode?.code === PaymentMethod.Ideal
  )
})

const getIsP24BankTransfer = createSelector(
  getSelectedPaymentMethodDetails,
  ({ paymentProvider }) => paymentProvider === PAYMENT_PROVIDERS.P24,
)

const getIsIdealBankTransfer = createSelector(
  getSelectedPaymentMethodDetails,
  ({ paymentProvider }) => paymentProvider === PAYMENT_PROVIDERS.IDEAL,
)

const getSelectedBankId = (state: RootState): string => state.bankTransfer.selectedBankId || ''

export const getRupayData = createSelector(getPaymentData, (paymentData) => paymentData?.rupayData)

export const getCreditGuardData = createSelector(
  getPaymentData,
  (paymentData) => paymentData?.creditGuardData,
)

const getCities = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState?.additionalInfo?.cities,
)

const getDistricts = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState?.additionalInfo?.districts,
)

// This is used for Serbia state as postalCode field is disabled
const getPostalCode = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState?.additionalInfo?.postalCode,
)

const getAdyenCardInstallments = createSelector(getPaymentData, (data) => {
  const adyenInstallments = data.adyenPaymentData?.adyenInstallmentMap
  if (!adyenInstallments) return []
  return Object.keys(adyenInstallments!).map((key) => ({
    label: adyenInstallments[key],
    value: key,
  }))
})

const getAdyenSelectedInstallment = createSelector(
  getCheckoutPayment,
  (paymentState) => paymentState.adyenInstallment,
)

const getNormalizedConsignmentDatas = createSelector(
  getCheckoutState,
  (checkoutState: CheckoutState) => {
    const tmpObject = {}
    checkoutState.delivery.consignmentDatas?.forEach((consignment) => {
      const key = consignment.consignmentType
      if (Object.keys(tmpObject).includes(key)) {
        tmpObject[key] = tmpObject[key].concat([consignment])
      } else {
        tmpObject[key] = [consignment]
      }
    })
    return Object.keys(tmpObject).map((val) => tmpObject[val])
  },
)

export const getMainConsignment = createSelector(getCheckoutState, (checkoutState) =>
  checkoutState.delivery.splitOrder?.parcels.find(
    (consignment) => consignment?.consignmentData?.mainConsignment,
  ),
)

export const getSecondaryConsignments = createSelector(getCheckoutState, (checkoutState) =>
  checkoutState.delivery.consignmentDatas?.filter((consignment) => !consignment.mainConsignment),
)

export const getIsPhoneNumberMandatoryForDelivery = createSelector(
  [getDeliveryModesSettings, (_, selectedDeliveryModeCode) => selectedDeliveryModeCode],
  (deliveryModes: Record<string, DeliveryMode>, selectedDeliveryModeCode: string): boolean => {
    return !!(deliveryModes?.[selectedDeliveryModeCode] as DeliveryMode)?.phoneNumberMandatory
  },
)

export const getIsPhoneNumberMandatoryOnParcels = createSelector(
  getDeliveryModesSettings,
  getSecondaryConsignments,
  (deliveryModes, consignments) => {
    if (!consignments || consignments.length === 0) return false
    const deliveryModesAsArray = Object.values(deliveryModes) as DeliveryMode[]
    return !!consignments.some((consignment) =>
      deliveryModesAsArray.some(
        (deliveryMode) =>
          deliveryMode.fulfilmentEntity === consignment.consignmentType &&
          deliveryMode.phoneNumberMandatory,
      ),
    )
  },
)

export const getConsignmentDataByWarehouseCode = createSelector(
  [getCheckoutState, (_, warehouseCode) => warehouseCode],
  (checkout: CheckoutState, warehouseCode: string): V2Consignment | undefined => {
    const consignmentDatas = checkout?.delivery?.consignmentDatas

    if (consignmentDatas) {
      return Object.values(consignmentDatas).find(
        (consignment) => consignment.warehouseCode === warehouseCode,
      )
    }

    return consignmentDatas
  },
)

export const getIsMainConsignmentValidated = createSelector(
  getCheckoutDelivery,
  (deliveryState) => !!deliveryState?.validatedMainConsignment,
)

export const getUndeliverableParcels = createSelector(
  getSplitOrder,
  getIsMainConsignmentValidated,
  (splitOrder, isMainConsignmentValidated) => {
    return (
      (isMainConsignmentValidated &&
        splitOrder.parcels.filter((f) => !f.deliveryAddress && !f.deliveryMode)) ||
      []
    )
  },
)

export const getParcelDeliveryAddressByParcelId = createSelector(
  [getSplitOrder, (_, parcelId) => parcelId],
  (splitOrder: NormalizedV2SplitOrder, parcelId: string): Address | undefined => {
    return splitOrder?.parcels?.find((parcel) => parcel.id === parcelId)?.deliveryAddress
  },
)

export const getUserHasEditableParcels = createSelector(
  getSplitOrder,
  getIsSplitDeliveryEnabled,
  getIsSplitDeliveryEnabledMap,
  (splitOrder: NormalizedV2SplitOrder, isSplitDeliveryEnabled, splitDeliveryMap) => {
    if (isSplitDeliveryEnabled) return true

    const hasConsignmentInDeliveryMap = !!splitOrder?.parcels?.find(
      (parcel) => splitDeliveryMap?.[parcel?.consignmentData?.consignmentType],
    )

    return hasConsignmentInDeliveryMap
  },
)

// Used in experiment - remove on cleanup - [COPAYNOL-2027]
export const getHasCompletePurchaseClicked = createSelector(
  getCheckoutState,
  (checkoutState) => !!checkoutState?.additionalInfo?.hasCompletePurchaseClicked,
)

// Used in experiment - remove on cleanup - [COWEB-2943]
export const getHasNordicHomeOrIBDelivery = createSelector(
  getSelectedDeliverySpeedCode,
  (deliveryMethodId) => {
    const deliveryCodes = [
      'home-slow-template-airmee-SE',
      'home-slow-template-budbee-SE',
      'selectable-point-slow-template-instabox-SE',
      'home-slow-template-postnord-DK',
      'home-slow-template-Postnord-NO',
      'home-slow-template-bring-NO',
      'home-slow-template-budbee-FI',
    ]
    return deliveryCodes.includes(deliveryMethodId)
  },
)

export const getHasOpenedDeliverySection = createSelector(
  getCheckoutState,
  (checkoutState) => !!checkoutState?.additionalInfo?.hasOpenedDeliverySection,
)

const getSelectedTimeSlot = createSelector(
  getCheckoutState,
  (checkoutState) => checkoutState.delivery.selectedTimeSlot,
)

export default {
  getIsContextLoaded,
  getCheckoutCustomer,
  getContextStatus,
  getDeliveryModeById,
  getValidatedAddresses,
  getIsValidated,
  getCheckoutErrors,
  getShouldShowErrorScreen,
  getCustomerDetailsStatus,
  getSelectedDeliveryMethod,
  getCheckoutCustomerPostalCode,
  getPickupPointsStatus,
  getSelectedDeliverySpeed,
  getSelectedDeliverySpeedCode,
  getSelectedPickupPointCode,
  getSelectedPickupPoint,
  getDeliveryModesByDeliveryMethod,
  getPickupPoints,
  getAvailablePickupPoints,
  getDeliveryMethodBy,
  getAvailableDeliverySpeeds,
  getIsDeliverySpeedSelectedByCode,
  getIsDeliverySpeedSectionCompleted,
  getIsDeliveryMethodSupportingHazmat,
  getShouldChangeDeliverySpeed,
  getShouldChangeDeliveryMethod,
  getCartItems,
  getCartItemsSortedByHazmat,
  getCartItemsQuantity,
  getCartUniqueItemsCount,
  getCartHazmatItems,
  getCartUndeliverableHazmatItems,
  getIsAllCartItemsHazmat,
  getHasHazmatItems,
  getAddressRequestStatus,
  getSelectedHomeDeliveryAddressId,
  getSelectedHomeDeliveryAddress,
  getCurrentBillingAddress,
  getCurrentDeliveryAddresses,
  getSelectedPaymentMethodCode,
  getSelectedPaymentMethod,
  getCashOnDeliveryDetails,
  getAvailablePaymentMethods,
  getSelectedPaymentMethodDetails,
  getHasCheckoutOrderTotalsData,
  getHasCheckoutCustomerData,
  getIsReturningCustomer,
  getIsMyInformationSectionComplete,
  getSelectedDeliveryAddress,
  getShouldShowProductsDetails,
  getNewsletterSubscription,
  getCustomerCellPhone,
  getIsReturningGuest,
  getDeliveryPhoneNumber,
  getIsDeliverySpeedSelected,
  getSplitOrder,
  getIsCustomerSelectedSpeed,
  getDeliverySpeedSelectionStatus,
  getIsSelectedDeliverySpeedSupportingHazmat,
  getPatchPaymentStatus,
  getPaymentError,
  getIsPaymentValidated,
  getIsPaymentUserInputNeeded,
  getIsDeliveryValidated,
  getAddressErrors,
  getLastUsedPickupPlacePostalCode,
  getPreviouslyUsedPickupPlace,
  getNearestPickupPoints,
  getPickupPointByCode,
  getShouldShowPreviouslyUsedPickupPoint,
  getAvailableCnCPoints,
  getCnCPointByCode,
  getCnCPointsStatus,
  getSelectedCnCPoint,
  getNearestCnCPoints,
  getShouldShowPreviouslyUsedCnCPoint,
  getPostProceedStatus,
  getIsAddressCompletelyBlacklisted,
  getIsReadyToPlaceOrder,
  getGiftCards,
  getGiftCardsStatus,
  getGiftCardTotalAmount,
  getIsPurchaseCoveredByGiftCards,
  getIsSelectedDeliveryPlaceSupportHazmat,
  getAdyenCreditCardValues,
  getCybersourceCreditCardValues,
  getHasKlarnaPaymentMethod,
  getKlarnaUpdateSessionBody,
  getSavedCards,
  getSelectedPaymentCardId,
  getSelectedPaymentCardInfo,
  getCardholderAddress,
  getIsNewGuest,
  getIsGuestCustomer,
  getCurrentAddressById,
  getSelectedPickupPointDetailsFromRawResponse,
  getPrepareStatus,
  getCart,
  getLoyaltyStatus,
  getOutOfStockItems,
  getPaymentData,
  getOmnicreditAuthCallbackEnabled,
  getIsBillingAddressInitiallyCompleted,
  getIsAllItemsOutOfStock,
  getIsAllItemsResellItemsOutOfStock,
  getIsResellAndOtherItemsOutOfStock,
  getIsSomeItemsOutOfStock,
  getAllDeliveryModesAsArray,
  getPrepopulatedPostalCode,
  getDeliveryAddress,
  getUI,
  getUIParcels,
  getParcelDeliveryInfoMain,
  getParcelDeliveryInfoCrossbrands,
  getParcelDeliveryInfoDropShip,
  getParcels,
  getItemsCountInParcels,
  getHasMultipleParcels,
  getIsDropShip,
  getIsOnlyOrderOrchestration,
  getDeliveryTitle,
  getCustomerTrackingData,
  getProductsTrackingData,
  getHasParcels,
  getCompletedSections,
  getUpdateDeliveryModesStatus,
  getIsValidDeliverySpeedSelected,
  getShouldShowDeliveryDetails,
  getCheckoutDelivery,
  getHmParcels,
  getOrderTotals,
  getVatNumber,
  getVatInvoiceType,
  getIsCODSupported,
  getCrossbrandsModalParcel,
  getIsSingleExternalParcel,
  getDropShipParcels,
  getAllCrossbrandParcels,
  getIsBankTransferSelected,
  getIsP24BankTransfer,
  getSelectedBankId,
  getIsIdealBankTransfer,
  getIsCrossBrandsBasket,
  getCustomerPhoneNumber,
  getCustomerPrefix,
  getCities,
  getDistricts,
  getHasResellItem,
  getHasOnlyResellItem,
  getResellItemsInCart,
  getAdyenCardInstallments,
  getAdyenSelectedInstallment,
  getCurrentAddresses,
  getRequestStatusByDeliveryMethod,
  getAdyenApplePayPaymentData,
  getSubTotal,
  getDeliveryModesSettings,
  getPaymentModesSettings,
  getApplePayAuthToken,
  getEditItemQuantityStatus,
  getParcelToShowInProductDetails,
  getPreselectedPickupPlace,
  getBOPISParcel,
  getIsBOPISInformationModalOpen,
  getIsBOPISSplitOrderModalOpen,
  getBOPISStoreData,
  getNormalizedConsignmentDatas,
  getIsBillingAddressSameAsDelivery,
  getSelectedTimeSlot,
  getPostalCode,
  getIsPaymentInitiallyCompleted,
}
